// src/pages/Account.jsx
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import toast from 'react-hot-toast';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { deleteUser, reauthenticateWithPopup, signOut } from 'firebase/auth';

import { auth, db, storage, googleProvider } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { useCountry } from '../contexts/CountryContext.jsx';
import { usePlanGuard } from '../hooks/usePlanGuard.js';
import { api } from '../api/citizencast.js';
import SEO from '../components/SEO.jsx';

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { allCountries } = useCountry();
  const plan = usePlanGuard();

  const [copiedKey, setCopiedKey] = useState(null); // 'code' | 'url' | null

  async function copyText(text, key) {
    if (!text) return;
    // 1차: navigator.clipboard (HTTPS + 권한 필요)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        toast.success(t('account.copied'));
        setTimeout(() => setCopiedKey(null), 1500);
        return;
      }
    } catch (e) {
      console.warn('clipboard.writeText failed, falling back to execCommand:', e);
    }
    // 2차: execCommand fallback (구식 브라우저 / in-app webview / 권한 거부 시)
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setCopiedKey(key);
        toast.success(t('account.copied'));
        setTimeout(() => setCopiedKey(null), 1500);
        return;
      }
      throw new Error('execCommand failed');
    } catch (e) {
      console.error('copy fallback failed:', e);
      toast.error(t('account.copyFailed'));
    }
  }
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const logoInputRef = useRef(null);

  async function handleDeleteAccount() {
    const confirmText = prompt(t('account.deleteConfirmPrompt'));
    if (confirmText !== t('account.deleteConfirmWord')) {
      if (confirmText !== null) toast.error(t('account.deleteConfirmMismatch'));
      return;
    }
    setDeleting(true);

    // 팝업 차단 회피: reauth 를 사용자 클릭 컨텍스트 안에서 먼저 실행.
    // 비동기 await 가 끼면 브라우저가 user gesture 만료로 간주해 popup 을 차단함.
    let reauthed = false;
    try {
      await reauthenticateWithPopup(auth.currentUser, googleProvider);
      reauthed = true;
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setDeleting(false);
        return; // 사용자가 취소
      }
      if (err.code === 'auth/popup-blocked') {
        toast.error(t('account.popupBlocked'));
        setDeleting(false);
        return;
      }
      console.error('reauth err:', err);
      toast.error(err.message || t('account.reauthFailed'));
      setDeleting(false);
      return;
    }

    try {
      // reauth 가 끝났으니 이제 비동기 작업 자유롭게.
      // 1) 서버: Polar/Firestore/Storage 정리
      await api.deleteAccount(user.uid);

      // 2) Firebase Auth 사용자 삭제 (방금 reauth 했으므로 recent-login 충분)
      await deleteUser(auth.currentUser);

      // 3) signOut 후 홈으로
      try { await signOut(auth); } catch { /* 이미 삭제됨 */ }
      toast.success(t('account.deleteDone'));
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('account.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }

  async function handleOpenPortal() {
    setOpeningPortal(true);
    try {
      const res = await api.customerPortal(user.uid);
      if (res.url) {
        window.open(res.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error(t('account.portalUrlFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('account.portalOpenFailed'));
    } finally {
      setOpeningPortal(false);
    }
  }

  async function handleSyncPlan() {
    setSyncing(true);
    try {
      const res = await api.syncPlan(user.uid, profile.email);
      toast.success(t('account.syncDone', { plan: res.plan.toUpperCase(), credits: res.credits }));
      // Firestore onSnapshot 으로 profile 자동 갱신
    } catch (err) {
      toast.error(err.message || t('account.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }

  if (!profile) return <div className="text-center py-12 text-gray-500">{t('account.loading')}</div>;

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error(t('account.logoTypeError'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('account.logoSizeError'));
      return;
    }
    setUploadingLogo(true);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `logos/${user.uid}.${ext}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file, { contentType: file.type });
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, 'users', user.uid), { channelLogoUrl: url });
      toast.success(t('account.logoUploadDone'));
    } catch (err) {
      console.error(err);
      toast.error(err.message || t('account.uploadFailed'));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }

  async function handleLogoRemove() {
    if (!confirm(t('account.logoRemoveConfirm'))) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), { channelLogoUrl: null });
      toast.success(t('account.logoRemoveDone'));
    } catch (err) {
      toast.error(err.message || t('account.removeFailed'));
    }
  }

  const referralUrl = `${window.location.origin}/register?ref=${profile.referralCode}`;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
      <SEO title={t('account.title')} description={t('account.seoDesc')} path="/account" noIndex />
      <h1 className="text-2xl font-bold">{t('account.title')}</h1>

      <div className="card space-y-2 text-sm">
        <div><span className="text-gray-500">{t('account.emailLabel')}:</span> {profile.email}</div>
        <div><span className="text-gray-500">{t('account.countryLabel')}:</span> {allCountries[profile.country]?.flag} {t(`countries.${profile.country}`)}</div>
        <div className="flex items-center justify-between">
          <span>
            <span className="text-gray-500">{t('account.planLabel')}:</span> <strong>{plan.plan.toUpperCase()}</strong>
          </span>
          <button
            onClick={handleSyncPlan}
            disabled={syncing}
            className="text-xs px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
            title={t('account.syncTitle')}
          >
            {syncing ? t('account.syncing') : t('account.syncBtn')}
          </button>
        </div>
        <div><span className="text-gray-500">{t('account.creditsLabel')}:</span> ⚡ {profile.credits ?? 0}</div>
      </div>

      {profile.referredBy && (!profile.referralRewarded || !profile.referrerRewarded) && (
        <div className="card border-2 border-amber-300 bg-amber-50 space-y-2">
          <p className="text-sm">
            <Trans
              i18nKey="account.referralFrom"
              values={{ code: profile.referredBy }}
              components={{ 1: <strong className="font-mono" /> }}
            />
            {!profile.referralRewarded && t('account.referralBonusUnpaid')}
            {!profile.referrerRewarded && t('account.referrerBonusUnpaid')}
          </p>
          <button
            className="btn-primary"
            onClick={async () => {
              try {
                const res = await api.processReferral(user.uid);
                console.log('[referral] retry result:', res);
                if (res.rewarded) toast.success(t('account.referralRetryDone'));
                else if (res.already) toast.success(t('account.referralAlready'));
                else if (res.capped) toast.error(t('account.referralCapped'));
                else toast.error(t('account.referralPartialFail', { detail: JSON.stringify(res.diag?.stages || res) }));
              } catch (e) {
                toast.error(e.message || t('account.referralRetryFailed'));
              }
            }}
          >
            {t('account.referralClaimBtn')}
          </button>
        </div>
      )}

      <div className="card space-y-3">
        <h2 className="font-bold">{t('account.referralTitle')}</h2>
        <p className="text-xs text-gray-500">{t('account.referralDesc')}</p>

        <div>
          <label className="text-xs text-gray-500 block mb-1">{t('account.codeLabel')}</label>
          <div className="flex gap-2">
            <input
              className="input font-mono text-sm"
              value={profile.referralCode}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <button
              className="btn-secondary whitespace-nowrap"
              onClick={() => copyText(profile.referralCode, 'code')}
              title={t('account.copyCode')}
            >
              {copiedKey === 'code' ? '✓' : t('account.codeBtn')}
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">{t('account.inviteLinkLabel')}</label>
          <div className="flex gap-2">
            <input
              className="input text-xs"
              value={referralUrl}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <button
              className="btn-secondary whitespace-nowrap"
              onClick={() => copyText(referralUrl, 'url')}
              title={t('account.copyLinkTitle')}
            >
              {copiedKey === 'url' ? '✓' : t('account.linkBtn')}
            </button>
          </div>
        </div>
      </div>

      {plan.canAddChannelLogo && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">{t('account.channelLogoTitle')}</h2>
            {profile.channelLogoUrl && (
              <button onClick={handleLogoRemove} className="text-xs text-red-500 hover:underline">
                {t('common.remove')}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">{t('account.channelLogoDesc')}</p>
          {profile.channelLogoUrl && (
            <img
              src={profile.channelLogoUrl}
              alt="channel logo"
              className="w-24 h-24 object-contain rounded-lg border bg-white"
            />
          )}
          <label className="btn-secondary inline-block cursor-pointer text-center">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
            />
            {uploadingLogo ? t('account.uploading') : profile.channelLogoUrl ? t('account.replaceLogo') : t('account.uploadLogo')}
          </label>
        </div>
      )}

      {plan.plan !== 'free' && (
        <button
          onClick={handleOpenPortal}
          disabled={openingPortal}
          className="btn-secondary w-full"
        >
          {openingPortal ? t('account.manageSubOpening') : t('account.manageSub')}
        </button>
      )}

      <Link to="/pricing" className="btn-primary w-full inline-block text-center">
        {t('pricing.upgradeBtn')}
      </Link>

      <div className="border-t pt-4 mt-6">
        <button
          onClick={handleDeleteAccount}
          disabled={deleting}
          className="w-full text-xs text-red-500 hover:text-red-700 disabled:opacity-50 py-2"
        >
          {deleting ? t('account.deleting') : t('account.deleteAccountBtn')}
        </button>
        <p className="text-[10px] text-gray-400 text-center mt-1">
          {t('account.deleteAccountNote')}
        </p>
      </div>

      {plan.isAdmin && (
        <div className="border-2 border-dashed border-purple-300 rounded-xl p-3 mt-4 space-y-2">
          <p className="text-xs font-bold text-purple-700">🛠 Admin · 사용자 강제 삭제</p>
          <p className="text-[10px] text-gray-500">이메일로 다른 사용자의 Auth + Firestore + Storage + Polar 구독을 모두 삭제. 복구 불가.</p>
          <button
            onClick={async () => {
              const target = prompt('삭제할 사용자 이메일:');
              if (!target) return;
              if (!confirm(`정말 ${target} 를 완전 삭제합니까? 복구 불가.`)) return;
              try {
                const res = await api.adminDeleteUser(profile.email, target.trim());
                toast.success(`삭제 완료: ${JSON.stringify(res.deleted)}`);
              } catch (e) {
                toast.error(e.message || 'admin 삭제 실패');
              }
            }}
            className="w-full text-xs text-purple-700 hover:bg-purple-50 py-2 rounded"
          >
            🗑 이메일로 사용자 삭제
          </button>
        </div>
      )}
    </div>
  );
}
